/**
 * Helpers for shift end view: 12-hour limit, overnight detection, manual end validation.
 */

const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

/** Return start-of-day (midnight local) for a date. */
function toDateOnly(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

/** True if the two dates are on different calendar days (local). */
export function isOvernight(shiftStart: Date, endDate: Date): boolean {
  return toDateOnly(endDate).getTime() !== toDateOnly(shiftStart).getTime();
}

/** Duration from shift start to end in hours. */
export function durationHours(shiftStart: Date, end: Date): number {
  return (end.getTime() - shiftStart.getTime()) / (1000 * 60 * 60);
}

/** True if shift would exceed 12 hours (forces manual end time). */
export function isExtendedShift(shiftStart: Date, end: Date): boolean {
  return durationHours(shiftStart, end) > 12;
}

/** Build ISO string from local date (YYYY-MM-DD) and time (HH:MM). */
export function manualEndToISO(dateStr: string, timeStr: string): string | undefined {
  if (!dateStr || !timeStr) return undefined;
  const [hours, minutes] = timeStr.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return undefined;
  const date = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(date.getTime())) return undefined;
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
}

/** True if manual end datetime is after start and within 12 hours of start. */
export function isManualEndWithin12Hours(
  shiftStart: Date,
  manualDateStr: string,
  manualTimeStr: string
): boolean {
  const iso = manualEndToISO(manualDateStr, manualTimeStr);
  if (!iso) return false;
  const end = new Date(iso);
  if (end.getTime() <= shiftStart.getTime()) return false;
  return end.getTime() - shiftStart.getTime() <= TWELVE_HOURS_MS;
}

/** Format date for display e.g. "Feb 13, 2024". */
export function formatShiftStartDate(isoStart: string): string {
  return new Date(isoStart).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Today in YYYY-MM-DD (local). */
export function todayDateString(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Convert Date to YYYY-MM-DD string (local). */
export function dateToDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Convert Date to HH:MM string (24-hour format). */
export function dateToTimeString(date: Date): string {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/** Return a Date that is exactly 12 hours after shift start. */
export function twelveHoursFromStart(shiftStart: Date): Date {
  return new Date(shiftStart.getTime() + TWELVE_HOURS_MS);
}
